import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { ProcessErrors } from '../../config/processErrors.ts';

const ChoreographerModal = ({ children, onClose, label, width }) => {
	const { Modal } = useChoreographerAssets();

	return (
		<Modal onClose={onClose} width={width} label={label}>
			{children}
		</Modal>
	);
};

const i18n = defineMessages({
	submitProcessWarningPopup: {
		id: 'import-from-sources.process-warning-popup.submitAction',
		defaultMessage: 'OK',
		description: "Submit button title in the 'process warning' popup",
	},
	imageErrorProcessWarningPopupTitle: {
		id: 'import-from-sources.process-warning-popup.imageErrorProcessPopupTitle',
		defaultMessage: 'Some images couldn’t be imported',
		description: "Title of 'process warning' popup in case of image process failure",
	},
	imageErrorProcessWarningPopupContent: {
		id: 'import-from-sources.process-warning-popup.imageErrorProcessWarningPopupContent',
		defaultMessage:
			'We ran into an issue importing one or more of the images in your document. We’ve created placeholders for them in your new page.',
		description: "Text content of 'process warning' popup in case of image process failure",
	},
	elementErrorProcessWarningPopupTitle: {
		id: 'import-from-sources.process-warning-popup.elementErrorProcessWarningPopupTitle',
		defaultMessage: 'Some elements couldn’t be imported',
		description: "Title of 'process warning' popup in case of ADF elements conversion failure",
	},
	elementErrorProcessWarningPopupContent: {
		id: 'import-from-sources.process-warning-popup.elementErrorProcessWarningPopupContent',
		defaultMessage:
			'We ran into an issue importing some elements of your document (images, fonts, etc.).',
		description:
			"Text content of 'process warning' popup in case of ADF elements conversion failure",
	},
});

const processWarningData = {
	[ProcessErrors.PROCESS_IMAGE_ERROR]: {
		heading: i18n.imageErrorProcessWarningPopupTitle,
		content: i18n.imageErrorProcessWarningPopupContent,
		analyticEvent: {
			type: 'sendUIEvent',
			data: {
				source: 'warningSomeImagesModal',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gotItImportWarningImages',
			},
		},
	},
	[ProcessErrors.ADF_CONVERSION_ERROR]: {
		heading: i18n.elementErrorProcessWarningPopupTitle,
		content: i18n.elementErrorProcessWarningPopupContent,
		analyticEvent: {
			type: 'sendUIEvent',
			data: {
				source: 'warningSomeElementsModal',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gotItImportWarningElements',
			},
		},
	},
};

type ProcessWarningPopupComponentProps = {
	onSubmit: () => void;
	errorType: ProcessErrors;
	onClose?: () => void;
};

export const ProcessWarningPopup: React.FC<ProcessWarningPopupComponentProps> = ({
	onSubmit,
	errorType,
	onClose,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onSubmitHandler = () => {
		createAnalyticsEvent(processWarningData[errorType].analyticEvent).fire();
		return onSubmit();
	};

	return (
		<ChoreographerModal
			onClose={onClose}
			width={420}
			label={processWarningData[errorType].heading.defaultMessage}
		>
			<ModalHeader hasCloseButton>
				<ModalTitle appearance="warning">
					<FormattedMessage {...processWarningData[errorType].heading} />
				</ModalTitle>
			</ModalHeader>

			<ModalBody>
				<FormattedMessage {...processWarningData[errorType].content} />
			</ModalBody>
			<ModalFooter>
				<Button
					appearance="primary"
					onClick={onSubmitHandler}
					testId="process-warning-popup-submit-button"
				>
					<FormattedMessage {...i18n.submitProcessWarningPopup} />
				</Button>
			</ModalFooter>
		</ChoreographerModal>
	);
};
